import { z } from 'zod';

import Ajv, { DefinedError, JSONSchemaType } from 'ajv';
import { ReactNode } from 'react';
import { ServiceError } from '../../ui/ErrorToErrorMessage';

/**
 * @deprecated
 * Use extractDefaultErrorMessageFromZodIssue instead
 */
function extractDefaultErrorMessage(e: DefinedError) {
    // for enums, we want to add the list of allowed values to the error message
    if (e.keyword === 'enum') {
        return `${e.message}: ${e.params.allowedValues.map((x) => `"${x}"`).join(', ')}`;
    }

    return e.message;
}

/**
 * @deprecated
 * Use createObjectParserFromZodSchema instead
 *
 * Creates a parser that parses an arbitrary "object" to a typed object by using
 * an Ajv schema definition.
 *
 * @param schema the Ajv schema definition for the object type
 * @returns a parser function
 */
export function createObjectParser<T>(
    schema: JSONSchemaType<T>,
    transformErrorMessage?: (error: DefinedError) => string | undefined
) {
    const ajv = new Ajv({
        discriminator: true,
        allowUnionTypes: true,
        allErrors: true,
        // Begin fix for IE (can remove in future once support for IE ends): https://github.com/ajv-validator/ajv/issues/1585
        unicodeRegExp: false,
        code: { es5: true },
        // End fix for IE
    });

    const validate = ajv.compile(schema);

    const stringifyError = (rootName: string, e: DefinedError, message?: string) => {
        return `"${rootName}${e.instancePath}": ${message ?? extractDefaultErrorMessage(e)}`;
    };

    return (data: unknown, rootName: string) => {
        if (validate(data)) {
            return data; // has correct type now
        } else {
            const errors = (validate.errors as DefinedError[]).map((e) => {
                if (!transformErrorMessage) {
                    return stringifyError(rootName, e);
                }

                return stringifyError(rootName, e, transformErrorMessage(e));
            });
            throw new ServiceError(errors);
        }
    };
}

/**
 * @deprecated
 * Use createStringParserFromZodSchema instead
 *
 * Creates a parser that parses an arbitrary string to a typed object by using
 * an Ajv schema definition.
 * @param schema the Ajv schema definition for the object type
 * @returns a parser function
 */
export function createStringParser<T>(
    schema: JSONSchemaType<T>,
    transformErrorMessage?: (error: DefinedError) => string | undefined
) {
    const parseJSON = (data: string, rootName: string) => {
        try {
            return JSON.parse(data) as unknown;
        } catch (e: any) {
            throw new ServiceError([`"${rootName}": ${e.message}`]);
        }
    };

    const parseObject = createObjectParser(schema, transformErrorMessage);

    return (data: string, rootName: string) => {
        return parseObject(parseJSON(data, rootName), rootName);
    };
}

/**
 * Creates a function based on the parserFunction
 * @param parserFunction createStringParser
 * @returns a function with the errors when exists
 */
export function createValidatorFromParser<T>(parserFunction: (data: T, rootName: string) => unknown) {
    return (data: T, rootName: string, errors: ReactNode[]) => {
        try {
            parserFunction(data, rootName);
        } catch (e: any) {
            e.validationErrors.forEach((error: any) => {
                errors.push(error);
            });
        }
    };
}

function extractDefaultErrorMessageFromZodIssue(e: z.ZodIssue) {
    // for enums, we want to add the list of allowed values to the error message
    if (e.code === 'invalid_enum_value') {
        return `Expected to be one of the allowed values: ${e.options.map((x) => `"${x}"`).join(', ')}. Received: "${
            e.received
        }".`;
    }

    return e.message;
}

/**
 * Creates a parser that parses an arbitrary "object" to a typed object by using
 * a Zod schema definition.
 */
export function createObjectParserFromZodSchema<TSchema extends z.ZodTypeAny>(
    schema: TSchema,
    transformErrorMessage?: (error: z.ZodIssue) => string | undefined
) {
    const stringifyError = (rootName: string, e: z.ZodIssue, message?: string) => {
        const path = e.path.join('/');
        return `"${rootName}${path.length > 0 ? '/' + path : ''}": ${
            message ?? extractDefaultErrorMessageFromZodIssue(e)
        }`;
    };

    return (data: unknown, rootName: string): z.infer<TSchema> => {
        const result = schema.safeParse(data);

        if (!result.success) {
            const errors = result.error.issues.map((issue) => {
                if (!transformErrorMessage) {
                    return stringifyError(rootName, issue);
                }

                return stringifyError(rootName, issue, transformErrorMessage(issue));
            });

            throw new ServiceError(errors);
        }

        return result.data;
    };
}

/**
 * Creates a parser that parses an arbitrary string to a typed object by using
 * a Zod schema definition.
 */
export function createStringParserFromZodSchema<TSchema extends z.ZodTypeAny>(
    schema: TSchema,
    transformErrorMessage?: (error: z.ZodIssue) => string | undefined
) {
    const parseJSON = (data: string, rootName: string) => {
        try {
            return JSON.parse(data) as unknown;
        } catch (e: any) {
            throw new ServiceError([`"${rootName}": ${e.message}`]);
        }
    };

    const parseObject = createObjectParserFromZodSchema(schema, transformErrorMessage);

    return (data: string, rootName: string) => {
        return parseObject(parseJSON(data, rootName), rootName);
    };
}
